/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import {
  Breadcrumbs,
  Typography,
  FormControl,
  Select,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useLocation } from "react-router-dom";
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useInfiniteQuery, keepPreviousData, useQuery } from "@tanstack/react-query";
import { PerPage } from "../../constants/perPage";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useNotificationContext } from "../../contexts/notificationContext";
import { StatusContainer } from "../audit_logs/styles";
import { Query_Keys } from "../../lib/queryKeys";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { FilterContainer } from "../../components/filter_container";
import { selectBaseStyles } from "../reports/styles";
import camelize from "../../utils/camelize";
import { menuPropStyle } from "../configured_discounts/styles";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index"
import { StyledMenuItem } from "../../components/styled_menu_item";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { ResetButton } from "../../components/reset_button";
import { formatDate } from "../../utils/formatDate";
import { UpdateButton } from "../../components/update_button";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { MenuHeading } from "../../components/menu_heading";
import { IndexTable } from "../../components/index_table";
import {
  useDebounce,
} from "../../utils/useDebounce";
import {
  getDropdownNextPageParam,
  normalizeRestaurantGroup,
  normalizeRestaurant,
  mergeUniqueByKey,
} from "../../utils/dropdownInfiniteQuery";
import {
  PageContainer,
  StyledBreadCrumbContainer,
  StyledLink,
  viewTextStyles,
  StyledInputLabel,
  StyledFilterCancel,
  datePickerStyles,
} from "../../components/page_layout_components/styles";
import { StyledSourceChip } from "./styles";
import { Loader } from "../../components/loader";
import { isValidDayjsValue } from "../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../utils/isValidCustomDateRange";

const defaultFilter = {
  restaurant_group_id: null,
  restaurant_ids: [],
  start_date: null,
  end_date: null
}

const splitCamelCase = (str) => {
  return str?.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const sourceLabels = {
  'seven_shifts': '7shifts'
}

export const ImportLogPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const singleRestaurantAutoApplied = useRef(false)
  const [filters, setFilters] = useState({
    restaurant_group_id: location?.state?.restaurant_group_id || null,
    restaurant_ids: location?.state?.restaurant_ids || [],
    start_date: location?.state?.start_date ? dayjs(location.state.start_date) : null,
    end_date: location?.state?.end_date ? dayjs(location.state.end_date) : null,
  })
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext()
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState('performed_at');
  const [order, setOrder] = useState('desc');
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      }
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const fetchImportLogs = async () => {
    const queryFilters = formatFilters()
    const response = await api.get('/v1/admin/import_sessions', { params: {
      ...queryFilters,
      per_page: itemsPerPage,
      page: page,
      sort_by: orderBy,
      sort_order: order
    }})

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data, isFetching } = useQuery({
    queryKey: Query_Keys.import_logs({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      order: order,
      orderBy: orderBy,
    }),
    queryFn: fetchImportLogs,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        restaurant_group_ids: filters?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups?.length === 1
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && restaurants?.length === 1;
  
  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'))
  }, [restaurants])

  useEffect(() => {
    if (singleRestaurantAutoApplied.current || !hasSingleAvailableRestaurant) {
      return
    }

    singleRestaurantAutoApplied.current = true
    setFilters(prev => ({...prev, restaurant_ids: [restaurants?.[0]?.id] }));
  }, [hasSingleAvailableRestaurant, restaurants])

  const handleRestaurantGroupChange = (event) => {
    setFilters(prev => ({...prev, restaurant_group_id: event.target.value, restaurant_ids: []}))
    setFiltersChanged(true)
    singleRestaurantAutoApplied.current = false
  }

  const handleRestaurantChange = (event) => {
    setFilters(prev => ({...prev, restaurant_ids: event.target.value}))
    setFiltersChanged(true)
    singleRestaurantAutoApplied.current = false
  }

  const handleDateChange = (date, key) => {
    setFilters((prev) => {
      if (key === 'start_date') {
        return {
          ...prev,
          start_date: date,
          end_date: !date 
                    ? null 
                    : (isValidDayjsValue(date) && !isValidDayjsValue(prev.end_date) ? dayjs().startOf("day") : prev.end_date),
        }
      }

      if (key === 'end_date') {
        return {
          ...prev,
          end_date: date
        }
      }
    });
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    setFilters(prev => ({...prev, restaurant_group_id: null, restaurant_ids: []}))
    setFiltersChanged(true)
    singleRestaurantAutoApplied.current = false
  }

  const handleRestaurantClear = () => {
    setFilters(prev => ({...prev, restaurant_ids: []}))
    setFiltersChanged(true)
    singleRestaurantAutoApplied.current = false
  }

  const handleReset = () => {
    setFilters(defaultFilter)
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setItemsPerPage(25)
    setPage(1)
  }

  const handleUpdate = () => {
    setAppliedFilters(filters)
    setFiltersChanged(false)
    setItemsPerPage(25)
    setPage(1)
  }

  const handleNavigate = (id) => {
    navigate(`/admin/import_logs/${id}`, { state: {
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_ids: filters?.restaurant_ids,
      start_date: filters?.start_date ? filters.start_date.toISOString() : null,
      end_date: filters?.end_date ? filters.end_date.toISOString() : null,
    }})
  }

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value  : ''
  }

  const getRestaurantDisplayValue = () => {
    if(filters?.restaurant_ids?.length === 0) return  []

    if (filters?.restaurant_ids?.length === 1) {
      const restaurant = restaurantOptionsCache?.find(r => r?.id === filters?.restaurant_ids?.[0]);
      return restaurant ? restaurant?.name : [];
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const isCustomDateRangeValid =
  (filters?.start_date == null || isValidDayjsValue(filters?.start_date)) &&
  (filters?.end_date == null || isValidDayjsValue(filters?.end_date)) &&
  (filters?.start_date == null || filters?.end_date == null || isValidCustomDateRange(filters?.start_date, filters?.end_date));

  const isUpdateDisabled = !isCustomDateRangeValid;

  const tableColumns = [
    { key: "id", sortable: true },
    { key: "source", sortable: true },
    { key: "performed_at", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "event_type", sortable: true },
    { key: "status", sortable: true },
    { key: "duration", sortable: true },
    { key: "no_of_retries", sortable: true },
    { key: "total_imports", sortable: true },
    { key: "auto_merged", sortable: true },
    { key: "needs_review", sortable: true },
    { key: 'actions', sortable: false },
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        source: {
          display: (
            <StyledSourceChip source={record?.source}>
              {sourceLabels[record?.source]}
            </StyledSourceChip>
          )
        },
        performed_at: formatDate(record?.performed_at, record?.time_zone_abbr),
        restaurant: record?.restaurant,
        event_type: splitCamelCase(record?.event_type),
        status: {
          display: (
            <StatusContainer status={record?.status}>
              {camelize(record?.status)}
            </StatusContainer>
          ),
          sortValue: record?.status,
        },
        duration: record?.duration,
        no_of_retries: record?.no_of_retries !== 0 ? record?.no_of_retries + 1 :  record?.no_of_retries,
        total_imports: record?.total_imports,
        auto_merged: record?.auto_merged,
        needs_review: record?.needs_review,
        actions: (
          <div>
            <Tooltip title='View' arrow placement="bottom">
              <VisibilityIcon 
                data-testid={`log-view-${record?.id}`}
                sx={{
                  color: '#707070',
                  cursor: 'pointer',
                }}
                onClick={() => handleNavigate(record?.id)}
              />
            </Tooltip>
          </div>
        )
      };
    });
  };

  const rows = TableRows(data?.data);

  const handleSortChange = (columnKey) => {
    const nextOrder = orderBy === columnKey ? (order === 'asc' ? 'desc' : 'asc') : 'asc'
    setOrderBy(columnKey)
    setOrder(nextOrder)
    setPage(1)
  }

  const formatFilters = (filterValues = appliedFilters) => {
    const params = {}
    if (filterValues?.restaurant_group_id) {
      params.restaurant_group_id = filterValues?.restaurant_group_id
    }

    if (filterValues?.restaurant_ids) {
      params.restaurant_id = filterValues?.restaurant_ids
    }

    if (isValidDayjsValue(filterValues?.start_date) && isValidDayjsValue(filterValues?.end_date)) {
      params.from_date = dayjs(filterValues?.start_date).format("DD/MM/YYYY")
      params.to_date = dayjs(filterValues?.end_date).format("DD/MM/YYYY")
    }

    return params
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true)
      const query = buildQueryParams(formatFilters())
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_import_sessions&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      const filename = `import_logs_${dayjs(new Date()).format('YYYY-MM-DD')}.csv`

      a.download = filename
      a.click()

      window.URL.revokeObjectURL(downloadUrl)

      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  return (
    <PageContainer>
      {/* Navigation Section */}
      <StyledBreadCrumbContainer data-testid='breadcrumb-section'>
        <Breadcrumbs>
        <NavigableBreadcrumbLink
          LinkComponent={StyledLink} 
          label="Home" to="/admin/dashboard"
          dataTestId={'home-breadcrumb-option'}
        />
        <StyledLink color={'#515152'}>
          Logs
        </StyledLink>
        <StyledLink color={'#515152'}>
          Import Logs
        </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Filters Section */}
      <FilterContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 2, md: 0 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "18px" },
              flexWrap: "wrap",
              width: { xs: "100%", md: "auto" },
              flex: 1
            }}
          >
            <Typography
              variant="body2"
              sx={viewTextStyles}
            >
                View
            </Typography>

            {/* Restaurant Group Filter */}
            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "180px" }
              }}
            >
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                data-testid='restaurant-group-filter'
                size="small"
                label="Restaurant Group"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantGroupMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "180px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                value={hasSingleAvailableRestaurantGroup ? restaurantGroups?.[0]?.label : (filters?.restaurant_group_id || "")}
                renderValue={() =>
                  hasSingleAvailableRestaurantGroup ? restaurantGroups?.[0]?.value : getRestaurantGroupName(filters?.restaurant_group_id)
                }
                endAdornment = {
                  filters?.restaurant_group_id ? (
                  <StyledFilterCancel position="end">
                    <IconButton
                      edge='end'
                      size="small"
                      disableRipple
                      onClick={handleRestaurantGroupClear}
                    >
                      <ClearIcon sx={{fontSize: '20px'}}/>
                    </IconButton>
                  </StyledFilterCancel>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                {restaurantGroups?.map((res) => (
                  <StyledMenuItem
                    key={res?.label}
                    value={res?.label}
                  >
                    {
                      hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>
            
            {/* Restaurant Filter */}
            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "180px" }
              }}
            >
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                data-testid='restaurant-filter'
                size="small"
                label="Restaurant"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "180px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                value={filters?.restaurant_ids || []}
                multiple
                renderValue={() => getRestaurantDisplayValue()}
                endAdornment={
                  filters?.restaurant_ids?.length > 0 ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                {restaurants?.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            {/* From date filter */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: { xs: "100%", sm: "auto" }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filters?.start_date}
                  onChange={(date) => handleDateChange(date, 'start_date')}
                  slotProps={{
                    textField: {
                      placeholder: "Date From",
                      inputProps: {
                        'data-testid': 'from-date-filter'
                      },
                    },
                  }}
                  sx={datePickerStyles}
                />
              </LocalizationProvider>
            </Box>
            
            {/* To date filter */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: { xs: "100%", sm: "auto" }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filters?.end_date}
                  onChange={(date) => handleDateChange(date, 'end_date')}
                  slotProps={{
                    textField: {
                      placeholder: "Date To",
                      inputProps: {
                        'data-testid': 'to-date-filter'
                      },
                    },
                  }}
                  sx={datePickerStyles}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              alignSelf: { xs: "flex-start", md: "center" },
              mt: { xs: 1, md: 0 },
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <ResetButton
              onClick={handleReset}
              filtersChanged={filtersChanged}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleUpdate}
              filtersChanged={filtersChanged}
              status={isFetching}
              disabled={isUpdateDisabled}
            />
          </Box>

        </Box>
      </FilterContainer>

      <MenuHeading report={false}>Import Logs</MenuHeading>
      
      {
        isFetching ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="import-log-entries"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={data?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={rows?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            serverSort={true}
            onSortChange={handleSortChange}
            serverOrderBy={orderBy}
            serverOrder={order}
          />
        )
      }
    </PageContainer>
  )
}
